type DateOrString = Date | string | number

export const DAY = 86400000
export const YEAR = 31536000000
const AVG_MONTH_DAYS = 30.4368 // średnia długość miesiąca (365.2425 / 12)
const today = new Date()

export const dateToYMDformat = (date = new Date()) => date.toLocaleDateString('en-CA') // 2023-01-22
const getDaysInMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()

export const toTime = (dateOrString: DateOrString, locales: Intl.LocalesArgument = 'pl') => {
  if (!dateOrString) return '-'
  const date = getDateObject(dateOrString)
  return date.toLocaleTimeString(locales, { hour: '2-digit', minute: '2-digit' })
}

export const toShortDate = (dateOrString: DateOrString, locales: Intl.LocalesArgument) => {
  if (!dateOrString) return '-'
  const date = getDateObject(dateOrString)
  const theSameYear = today.getFullYear() === date.getFullYear()
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short'
  }
  if (!theSameYear) {
    options.year = 'numeric'
  }
  return date.toLocaleDateString(locales, options)
}

export const toLongDate = (dateOrString: DateOrString, locales: Intl.LocalesArgument, monthUsingNumbers = false) => {
  if (!dateOrString) return '-'
  const date = getDateObject(dateOrString)
  return date.toLocaleDateString(locales, { day: '2-digit', month: monthUsingNumbers ? '2-digit' : 'long', year: 'numeric' })
}

export const toDateTime = (dateOrString: DateOrString, locales: Intl.LocalesArgument) => {
  if (!dateOrString) return '-'
  const date = getDateObject(dateOrString)
  return date.toLocaleString(locales, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const getDateObject = (dateOrString: DateOrString) => {
  return typeof dateOrString === 'object' ? dateOrString : new Date(dateOrString)
}

export const itIsToday = (dateOrString: DateOrString) => {
  if (!dateOrString) return '-'
  const date = getDateObject(dateOrString)
  return (
    today.getDate() === date.getDate()
    && today.getMonth() === date.getMonth()
    && today.getFullYear() === date.getFullYear()
  )
}

export const getEndDate = ({ m = 0, d = 0 }, startDate?: DateOrString, returnDateObject = false, mode = 'sameDay') => {
  if (m < 0) return undefined

  const date = getDateObject(startDate || new Date())
  const originalDay = date.getDate()
  const startIsEOM = getDaysInMonth(date)

  date.setDate(1)
  date.setMonth(date.getMonth() + m)

  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()

  if (mode === 'endOfMonth' && startIsEOM) {
    date.setDate(lastDay)
  } else {
    date.setDate(Math.min(originalDay, lastDay))
  }

  date.setDate(date.getDate() + d)

  return returnDateObject
    ? date
    : dateToYMDformat(date)
}

export const getRangeFromDates = (endDate: DateOrString = new Date(), startDate: DateOrString = new Date(), mode = 'sameDay') => {
  const start = getDateObject(startDate)
  const end = getDateObject(endDate)

  if (end < start) return { m: 0, d: 0 }

  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())

  const startIsEOM = getDaysInMonth(start)
  const endIsEOM = getDaysInMonth(end)

  const anchor = new Date(start)
  anchor.setDate(1)
  anchor.setMonth(anchor.getMonth() + months)

  const lastDay = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate()

  if (mode === 'endOfMonth' && startIsEOM) {
    anchor.setDate(lastDay)
  } else {
    anchor.setDate(Math.min(start.getDate(), lastDay))
  }

  // korekta
  if (anchor > end) {
    months--

    anchor.setMonth(anchor.getMonth() - 1)

    const lastDay = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate()

    if (mode === 'endOfMonth' && startIsEOM) {
      anchor.setDate(lastDay)
    } else {
      anchor.setDate(Math.min(start.getDate(), lastDay))
    }
  }

  if (mode === 'endOfMonth' && startIsEOM && endIsEOM) {
    return { m: months, d: 0 }
  }

  const msPerDay = 24 * 60 * 60 * 1000
  const days = Math.round((end.getTime() - anchor.getTime()) / msPerDay)

  return { m: months, d: days }
}

// Data końca umowy dla okresu { m, d } od startu.
// rentalMonthMinusOneDay (flaga placu z checkPrice): koniec = start + okres − 1 dzień (np. 02.03 → 01.04 zamiast 02.04).
export function getContractEndDate(range: { m?: number, d?: number }, startDate: DateOrString | undefined, minusOneDay: boolean | undefined, returnDateObject: true): Date | undefined
export function getContractEndDate(range: { m?: number, d?: number }, startDate?: DateOrString, minusOneDay?: boolean, returnDateObject?: false): string | undefined
export function getContractEndDate(range: { m?: number, d?: number }, startDate?: DateOrString, minusOneDay = false, returnDateObject = false) {
  const start = toLocalMidnight(startDate || new Date()) // nowy obiekt (getEndDate mutuje Date) + poprawny dzień dla 'YYYY-MM-DD'
  const date = getEndDate(range, start, true) as Date | undefined
  if (!date) return undefined
  if (minusOneDay) date.setDate(date.getDate() - 1)
  return returnDateObject ? date : dateToYMDformat(date)
}

// Odwrotność getContractEndDate: okres { m, d } dla daty końca umowy.
// Przy rentalMonthMinusOneDay pełne miesiące liczone są z endDate + 1 (tak jak backend).
export const getContractRange = (endDate: DateOrString, startDate: DateOrString, minusOneDay = false) => {
  if (!minusOneDay) return getRangeFromDates(endDate, startDate)
  // obie daty jako lokalna północ — inaczej 'YYYY-MM-DD' (UTC) vs czas lokalny psuje wynik przy zmianie czasu (np. 20.10 → 19.11)
  const start = toLocalMidnight(startDate)
  const end = toLocalMidnight(endDate)
  end.setDate(end.getDate() + 1)
  return getRangeFromDates(end, start)
}

const toLocalMidnight = (dateOrString: DateOrString) => {
  if (typeof dateOrString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateOrString)) {
    const [y, m, d] = dateOrString.split('-').map(Number)
    return new Date(y, m - 1, d)
  }
  const date = getDateObject(dateOrString)
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

const toDays = (m: number, d: number) => {
  return m * AVG_MONTH_DAYS + d
}

export const findClosestRecord = (table: { m: number, d: number }[], target: { m: number, d: number }) => {
  const targetDays = toDays(target.m, target.d)

  let best = null
  let bestDiff = Infinity

  for (const record of table) {
    const recordDays = toDays(record.m, record.d)
    const diff = Math.abs(recordDays - targetDays)

    if (diff < bestDiff) {
      bestDiff = diff
      best = record
    }
  }

  return best
}